import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { describeApiError, isApiError } from "../../api/errors";
import {
  useAcceptSuggestions,
  useRemap,
  useAccountMappings,
  useAllReviewItems,
  useApproveMapping,
  useContactMerges,
  useMigration,
  useResolveReviewItem,
  useReviewItems,
  useScope,
  useSignoff,
  useSignoffWatch,
  useTaxMappings,
} from "../../api/hooks";
import { formatPauseAt, type MigrationState, type ReviewItem } from "../../api/types";
import {
  ActionsFirst,
  Disclosure,
  Banner,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Spinner,
} from "../../components";
import { vendorName } from "../../i18n/oauthCopy";
import { t } from "../../i18n/t";
import { MigrationPage } from "../migrationPage";
import { isMappingNotReady, stageForState } from "../stageRoute";
import { HmrcVerificationPanel } from "../status/HmrcVerificationPanel";
import { CoaTable, type CoaRow } from "./CoaTable";
import { ContactMerges, type MergeRow } from "./ContactMerges";
import { DimensionLoss } from "./DimensionLoss";
import { OtherReviewItems } from "./OtherReviewItems";
import { ReviewFilters, TABS, type TabKey, useReviewFilters } from "./ReviewFilters";
import { VatQueue, type TaxRow } from "./VatQueue";
import { gatesCurrentSignoff, resolveMayMoveState } from "./gating";
import { needsChoice, suggestionCount, suggestionOf } from "./suggestions";

/** The four dedicated panels' own kinds; anything else renders in `OtherReviewItems` instead --
 * UNLESS it is one of these kinds but has no CURRENT mapping version (`mapping_version_id === null`),
 * per finding 4 below, in which case none of the four panels can render it either. `COA` is further
 * split by severity (T8 follow-ups, MEDIUM 3): see `isPanelItem` below. */
const PANEL_KINDS: ReadonlySet<string> = new Set(["COA", "TAX", "CONTACT", "DIMENSION"]);

/**
 * T8 round-4 review fix (finding 4): `_scope_review_items` files kind="COA" scope BLOCK items
 * (`depreciation_source_type_unpinned`, `fixed_assets_non_gbp`) with `mapping_version_id=None` -- they
 * are not mapping rows at all, so `CoaTable` (fed from the `accounts`/`coa_mappings` endpoint) has no
 * row for their key, and `PANEL_KINDS` used to route every `kind: "COA"` item away from the generic
 * panel regardless. Since the widened `current` filter (round 3) started counting these NULL-version
 * rows in `blockingCount`, that left an item that counts against Approve with NO control anywhere on
 * the page able to resolve it. A dedicated-panel `kind` only genuinely has a home in one of the four
 * panels when it also carries a mapping version; a NULL-version row of that same `kind` falls through
 * to the generic panel instead, exactly like a `kind` the four panels never claimed at all.
 *
 * T8 follow-ups (MEDIUM 3): a CURRENT-version `COA` `BLOCK` item (a target-code collision or an
 * unmatched role, both from `mapping/coa.py`'s `map_accounts`) is ALSO routed to the generic panel.
 * `CoaTable` renders mapping rows, not review items: it has no control that can resolve a review item
 * at all, so a COA BLOCK item stuck counting against `blockingCount` with `CoaTable` as its only
 * "home" was, in practice, unresolvable from the web. The generic panel resolves by item id (`repo.
 * resolve_review` still writes the SAME `coa_mappings` row through `target_table`/`target_key`).
 *
 * coa-reuse-name-guard: a COA WARN that offers a REAL choice (>= 2 options -- the name guard's own
 * "reuse or create" decision, `mapping/account_names.py`) is ALSO routed to the generic panel, for the
 * same reason as the BLOCK case just above: `CoaTable` has no resolve control at all, so a two-option
 * WARN stuck there was unresolvable from the web even though it does not count against `blockingCount`
 * (WARNs never block). A single-option COA WARN (the pre-existing "confirm the merge"/"renumbered to"
 * shape) is unchanged and stays in `CoaTable`.
 *
 * xero-codeless-bank #18 Part C: a single-option COA WARN badged `"DERIVED_CODE"`
 * (`mapping.coa.annotate_derived_codes`) is ALSO routed to the generic panel, for the OPPOSITE reason to
 * the two cases above: `CoaTable` renders mapping ROWS, not review-item MESSAGE text, so a code
 * LuciMigration derived for the operator (no Xero account code existed) would otherwise render as an
 * ordinary, unexplained mapping row with nothing to say why this account was renumbered at all -- the
 * `message` this item exists to show never has anywhere to display. `OtherReviewItems` shows the
 * message plus the item's own "keep" option instead.
 */
function isPanelItem(item: ReviewItem): boolean {
  if (item.mapping_version_id === null) {
    return false;
  }
  if (item.kind === "COA") {
    if (item.badge === "DERIVED_CODE") {
      return false;
    }
    // XERO-MAP-v1: the adopt mapper's migration-report WARNs (a note about an account it placed, never a choice).
    // The chart table shows mapping rows, not item text, so these render with the other notes instead.
    if (item.badge === "ADOPT_REPORT") {
      return false;
    }
    return item.severity !== "BLOCK" && item.options.length < 2;
  }
  return PANEL_KINDS.has(item.kind);
}

function isResolved(item: ReviewItem): boolean {
  return item.resolved_at !== null && item.resolved_at !== undefined;
}

/**
 * T8 deferred items 2/3 (design §7.3/§7.5): `items` is `allItems.data`, the server's own newest-id-first
 * order (`list_review_items`'s `ReviewItemRow.id.desc()`) -- so the FIRST item seen per `(kind, key)` is
 * already the newest one. Per key, an unresolved item always wins over a resolved one (there is at most
 * one open item per key in the current mapping version), and among resolved-only keys the newest one is
 * kept. This is "the row's own item" both `ContactMerges` and `VatQueue` render (I5, I8, I11).
 */
function itemsByKey(items: readonly ReviewItem[], kind: string): Map<string, ReviewItem> {
  const newestByKey = new Map<string, ReviewItem>();
  const openByKey = new Map<string, ReviewItem>();
  for (const item of items) {
    if (item.kind !== kind) {
      continue;
    }
    if (!newestByKey.has(item.key)) {
      newestByKey.set(item.key, item);
    }
    if (!isResolved(item) && !openByKey.has(item.key)) {
      openByKey.set(item.key, item);
    }
  }
  for (const [key, item] of openByKey) {
    newestByKey.set(key, item);
  }
  return newestByKey;
}

/** As in the panels, each entry translates at the call site so the "(b)" i18n assertion can see the reference.
 * The count is NOT baked into the label: it renders as its own mono span in the underline tab (Review.html). */
const TAB_LABELS: Readonly<Record<TabKey, () => string>> = {
  coa: () => t("review.tab_accounts"),
  vat: () => t("review.tab_tax"),
  contacts: () => t("review.tab_contacts"),
  dimensions: () => t("review.tab_dimensions"),
};

const TAB_OF_KIND: Readonly<Record<string, TabKey>> = {
  COA: "coa",
  TAX: "vat",
  CONTACT: "contacts",
  DIMENSION: "dimensions",
};

interface AlreadyResolved {
  resolved_option?: string;
  resolved_by?: string;
  resolved_at?: string;
}

/**
 * §B4. Four panels, one endpoint each, one filter bar, one footer. This component owns every query and every
 * mutation; the four panels are presentational.
 */
export function ReviewScreen(): JSX.Element {
  const { migrationId } = useParams();
  const id = migrationId ?? "";
  const { filters, setTab, openTab, query } = useReviewFilters();

  const migration = useMigration(id === "" ? null : id);
  const scope = useScope(id);
  const accounts = useAccountMappings(id);
  const taxes = useTaxMappings(id);
  const contacts = useContactMerges(id);
  const dimensions = useReviewItems(id, { ...query, kind: "DIMENSION" });
  // T8 round-3 review fix (finding 7): a single unfiltered, exhaustive snapshot -- not two separate
  // cursor walks (the former `blocking`/`otherAll`) -- so `blockingCount`, `otherItems` and the key
  // lookup behind `onResolveByKey` can never read two different pages of the SAME migration's review
  // items (I12). `blockingItems`/`blockingCount` are DERIVED below, the same way `otherItems` already
  // was, rather than fetched with their own `severity=BLOCK&resolved=false` query: that is also what
  // T8 follow-ups' MEDIUM 2 fix (the paging tests) proves stays a SINGLE walk across a resolve, not two
  // (row 44).
  const allItems = useAllReviewItems(id, {});

  const resolve = useResolveReviewItem(id);
  const accept = useAcceptSuggestions(id);
  const remap = useRemap(id);
  const approve = useApproveMapping(id);
  const signoff = useSignoff(id);
  const signoffWatch = useSignoffWatch(id, migration.data?.state);
  // Sign-off and approve-only race each other (both approve the version): either in flight, or accepted and
  // waiting for the state to move, locks both.
  const approvalBusy = signoff.isPending || approve.isPending || signoffWatch.waiting;

  const [conflict, setConflict] = useState<AlreadyResolved | null>(null);
  const [confirming, setConfirming] = useState(false);
  // xmg-actions-first: the mappings (chart, VAT, contacts, dimensions) sit in a collapsed section below the
  // questions. A link that names a tab (`?tab=`) asks for it open; "Open the tab to choose" opens it too.
  const [searchParams] = useSearchParams();
  const [mappingsOpen, setMappingsOpen] = useState(searchParams.has("tab"));
  // xmg-coafast: what the last "Accept all suggestions" left behind (refused by the single-resolve path, or a
  // stale mapping), shown as one banner; cleared by the next click.
  const [acceptNote, setAcceptNote] = useState<string | null>(null);
  // xmg-webux (defect 2): the row "Open the tab to choose for <code>" points at. Switching the tab alone
  // did nothing visible -- the tab bar sits far above the button -- so the screen also scrolls the panel
  // to the row, focuses it and highlights it.
  const [target, setTarget] = useState<{ tab: TabKey; key: string } | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const targetRowsReady =
    target === null
      ? false
      : target.tab === "coa"
        ? accounts.data !== undefined
        : target.tab === "vat"
          ? taxes.data !== undefined
          : target.tab === "contacts"
            ? contacts.data !== undefined
            : dimensions.data !== undefined;
  useEffect(() => {
    if (target === null || filters.tab !== target.tab) {
      return;
    }
    const panel = panelRef.current;
    if (panel === null) {
      return;
    }
    const row = Array.from(panel.querySelectorAll<HTMLElement>("[data-rowkey]")).find(
      (element) => element.getAttribute("data-rowkey") === target.key,
    );
    if (row === undefined) {
      // The rows are still loading (or the key is not on this tab): at least bring the panel into view.
      panel.scrollIntoView?.({ behavior: "smooth", block: "start" });
      return;
    }
    row.scrollIntoView?.({ behavior: "smooth", block: "center" });
    row.focus({ preventScroll: true });
  }, [target, filters.tab, targetRowsReady]);

  if (migration.isLoading) {
    return <Spinner />;
  }

  const state = (migration.data?.state ?? "draft") as MigrationState;
  // Extraction / mapping still running: no mapping version exists, so there is nothing to review, accept or
  // sign off. The page says so plainly and offers no action; the server refuses these calls in that state too.
  if (isMappingNotReady(state)) {
    const pause = migration.data?.pause;
    return (
      <MigrationPage
        id={id}
        migration={migration.data}
        title={t("review.title")}
        meta={t("migrationheader.meta_route", {
          source: vendorName(migration.data?.source_system ?? ""),
          destination: vendorName(migration.data?.destination_system ?? ""),
        })}
      >
        <div data-testid="review-not-ready">
          <EmptyState
            title={t("review.not_ready_title")}
            body={
              pause === null || pause === undefined
                ? `${t("review.not_ready_running")} ${t("review.not_ready_body")}`
                : `${t("review.not_ready_paused", { at: formatPauseAt(pause.until) })} ${t("review.not_ready_body")}`
            }
          />
        </div>
      </MigrationPage>
    );
  }
  const readOnly = stageForState(state) !== "review";
  // §B4.6: `auto_approve_mapping` is a §B3.3 scope key, so it is read off the frozen scope rather than off a
  // migration column that §A8 does not promise.
  const autoApproved =
    (scope.data?.scope as Record<string, unknown> | undefined)?.auto_approve_mapping === true;
  const versionId = accounts.data?.mapping_version_id ?? taxes.data?.mapping_version_id ?? null;
  // I13: the server's own `open_blocking_reviews` set is "current mapping version or NULL, BLOCK,
  // unresolved" -- `allItems` already carries only `mapping_version=current` rows (the endpoint's own
  // default, unchanged), so filtering by severity and resolution here is the whole of it.
  const blockingItems = (allItems.data ?? []).filter(
    (item) => item.severity === "BLOCK" && (item.resolved_at === null || item.resolved_at === undefined),
  );
  const blockingCount = blockingItems.length;
  const shownBlocking = migration.data?.open_blocking_reviews ?? blockingCount;
  // T8 round-2 review fix: the ones whose `kind` has no dedicated tab (SOURCE, VAT_LOCK, CHUNKING,
  // HYPERCARE, ...) -- and now (T8 follow-ups, MEDIUM 3) a current-version COA BLOCK item too -- are
  // rendered here so they can actually be resolved from the web instead of only disabling Approve with
  // no control anywhere on the page.
  // xmg-coafast: an open item that needs a real choice is listed FIRST, at the top of the page, whatever its
  // kind and whichever tab is open; the lower generic panel keeps only the rest.
  // Items a dedicated tab already renders with its own controls are pointed at (a button opens the tab), not
  // duplicated; the rest carry their resolve controls right here.
  const choiceItems = (allItems.data ?? []).filter(needsChoice);
  const choiceInTabs = choiceItems.filter(isPanelItem);
  const otherItems = (allItems.data ?? []).filter((item) => !isPanelItem(item));
  // T8 deferred items 2/3 (design §7.3, §7.5): each row's OWN item of the given `kind` -- an unresolved
  // one of ANY severity (BLOCK or WARN), else the newest resolved one for that key -- so a row always
  // has something to show (I11) and `onResolveByKey` below never targets a resolved or wrong-kind item.
  // Superseded findings this replaces: the old `taxBlockOptionsByCode` only ever looked at `blockingItems`
  // (BLOCK, unresolved TAX only), which is why a WARN TAX item had no control (T5) and why the CONTACT
  // tab's Accept/Reject never found anything to POST to at all (2a) -- `blockingItems` never contains a
  // CONTACT item, because the mapper files every CONTACT item as WARN (`mapping/contacts.py`).
  const taxItemByCode = itemsByKey(allItems.data ?? [], "TAX");
  const contactItemByKey = itemsByKey(allItems.data ?? [], "CONTACT");

  /**
   * §B4.5. One mutation, then the invalidations 06a's hook already declares — the review-items query **and** the
   * three mapping queries — so the row re-renders with its new `target_code` and `status="MAPPED"`. That
   * re-render is the write-through proof; this handler only routes the 409.
   */
  async function onResolveItem(itemId: string, option: string): Promise<void> {
    setConflict(null);
    try {
      await resolve.mutateAsync({
        itemId,
        body: { option },
        mayMoveState: resolveMayMoveState(state, option),
      });
    } catch (error) {
      if (isApiError(error) && error.is("already_resolved")) {
        setConflict(error.details as AlreadyResolved);
        return;
      }
      throw error;
    }
  }

  /**
   * xmg-coafast: one click for every open item that carries a suggestion. The server resolves them through the
   * single-resolve path with the signed-in operator as actor; the screen only sends the mapping version it is
   * showing (a stale one is a 409, so nothing the operator did not see is accepted).
   */
  async function onAcceptAll(versionIdToAccept: string): Promise<void> {
    setConflict(null);
    setAcceptNote(null);
    try {
      const result = await accept.mutateAsync({
        mappingVersionId: versionIdToAccept,
        mayMoveState: resolveMayMoveState(state, ""),
      });
      if (result.refused.length > 0) {
        setAcceptNote(t("review.accept_refused", { n: result.refused.length }));
      }
    } catch (error) {
      if (isApiError(error)) {
        setAcceptNote(describeApiError(error, t("error.unexpected")));
        return;
      }
      throw error;
    }
  }

  /** xmg-coafast2: the hint's own action -- re-runs the mapping so the items get suggestions. */
  async function onRemap(): Promise<void> {
    setAcceptNote(null);
    try {
      await remap.mutateAsync({});
    } catch (error) {
      if (isApiError(error)) {
        setAcceptNote(describeApiError(error, t("error.unexpected")));
        return;
      }
      throw error;
    }
  }

  /**
   * The VAT and contact panels address rows by their business key; the review item carries it as `key`.
   * T8 deferred items 2/3 (design §7.3/§7.5, carrying forward critique C10's kind-AND-key match): looked
   * up through each kind's own map (built from EVERY current-version item, not only `blockingItems`), so
   * a CONTACT WARN item and a TAX WARN item are both resolvable, and a business key that collides across
   * kinds (COA/CONTACT/TAX key spaces are all, in general, plain codes) never resolves the wrong item
   * (I5). Only the row's OPEN item may be targeted: a resolved or missing item is a silent no-op, exactly
   * as a not-found item was before.
   */
  async function onResolveByKey(kind: string, key: string, option: string): Promise<void> {
    const item =
      kind === "TAX" ? taxItemByCode.get(key) : kind === "CONTACT" ? contactItemByKey.get(key) : undefined;
    if (item === undefined || isResolved(item)) {
      return;
    }
    await onResolveItem(item.id, option);
  }

  const coaRows = (accounts.data?.items ?? []) as unknown as readonly CoaRow[];
  // T8 deferred item 3 (design §7.4): `row.options` first (a legacy `review.test.tsx` fixture that
  // already carries it wins here, unchanged, T4), else the options of the row's own OPEN TAX item, of
  // ANY severity (T3/T5) -- never a resolved item's stale options (T6). The message is the row item's
  // message regardless of resolution (T6: a resolved item's text stays visible), or `null` when the row
  // has none (T2/C3). `TaxMapping` structurally satisfies `TaxRow` (every added field is optional), so a
  // single, ordinary assertion carries the real wire shape through with no `unknown` bridge.
  const taxRows: readonly TaxRow[] = (taxes.data?.items ?? []).map((row) => {
    const typed = row as TaxRow;
    const item = taxItemByCode.get(typed.source_code);
    const open = item !== undefined && !isResolved(item);
    return {
      ...typed,
      options: typed.options ?? (open ? item.options : undefined),
      message: item?.message ?? null,
      // xmg-coafast: the open item's suggestion starts the select; a resolved one suggests nothing.
      suggested_option: item !== undefined ? suggestionOf(item) : null,
      summary: item?.summary ?? null,
      severity: open ? item.severity : null,
    };
  });
  // T8 deferred item 2a (design §7.5): `decidable` is true only while the row's own CONTACT item is
  // still open (C3/C4's tier-0-has-no-item case, and C4's "already resolved" case, both give `false`) --
  // `ContactMerges` disables both buttons whenever it is not `true` (I5: no enabled no-op control).
  const mergeRows: readonly MergeRow[] = (contacts.data?.items ?? []).map((row) => {
    const typed = row as MergeRow;
    const item = contactItemByKey.get(typed.source_ext_id);
    const open = item !== undefined && !isResolved(item);
    return { ...typed, decidable: open, message: item?.message ?? null };
  });
  const dimensionItems = dimensions.data?.items ?? [];
  // xmg-coafast: the number the header button shows is the number the server will resolve.
  const suggestions = suggestionCount(allItems.data ?? []);
  // xmg-coafast2: the mapping predates suggestions AND something open offers options with none suggested.
  const suggestionsMissing =
    accounts.data?.suggestions_missing === true && !readOnly && choiceItems.length > 0;

  const otherPanel = {
    items: otherItems,
    tabChoices: choiceInTabs,
    onOpenTab: (item: ReviewItem) => {
      const tab = TAB_OF_KIND[item.kind] ?? "coa";
      // The mappings sit in a collapsed section: opening a tab opens it too.
      setMappingsOpen(true);
      openTab(tab);
      setTarget({ tab, key: item.key });
    },
    // A read-only stage still lets the operator answer an open BLOCK that refuses THIS stage's sign-off
    // (scope, mapping, hypercare); everything else stays read-only.
    disabled: (item: ReviewItem) => readOnly && !gatesCurrentSignoff(item, state),
    onResolve: (itemId: string, option: string) => void onResolveItem(itemId, option),
  };
  const reviewTop = <OtherReviewItems {...otherPanel} />;

  const targetKey = target !== null && target.tab === filters.tab ? target.key : null;

  const counts: Record<TabKey, number> = {
    coa: coaRows.length,
    vat: taxRows.length,
    contacts: mergeRows.length,
    dimensions: dimensionItems.length,
  };

  const mappingCounts = [
    t("review.mappings_accounts", { n: counts.coa }),
    t("review.mappings_tax", { n: counts.vat }),
    t("review.mappings_contacts", { n: counts.contacts }),
    t("review.mappings_dimensions", { n: counts.dimensions }),
  ].join(", ");

  const questions = (
    <>
      {conflict === null ? null : (
        <Banner tone="warn" title={t("error.code.already_resolved")}>
          <span data-testid="review-conflict">
            {t("review.resolved_by", {
              actor: conflict.resolved_by ?? t("review.actor_unknown"),
              target: conflict.resolved_option ?? "",
            })}
          </span>
        </Banner>
      )}
      {acceptNote === null ? null : (
        <Banner tone="warn">
          <span data-testid="review-accept-note">{acceptNote}</span>
        </Banner>
      )}

      {/* xmg-coafast2: ONE "Needs your choice" section (in-tab choices and the others together), at the very
          top; the notes and acknowledgements and the mappings follow, collapsed. */}
      <div className="row">
        <Button
          variant="primary"
          disabled={readOnly || versionId === null || suggestions === 0 || accept.isPending}
          onClick={() => {
            if (versionId !== null) {
              void onAcceptAll(versionId);
            }
          }}
        >
          {t("review.accept_all", { n: suggestions })}
        </Button>
        {choiceItems.length === 0 ? null : (
          <span data-testid="review-choice-count">
            {t("review.needs_choice_count", { n: choiceItems.length })}
          </span>
        )}
      </div>

      {suggestionsMissing ? (
        <Banner tone="info">
          <span data-testid="review-suggestions-missing">{t("review.suggestions_missing")}</span>{" "}
          <Button disabled={remap.isPending || remap.isSuccess} onClick={() => void onRemap()}>
            {t("review.remap")}
          </Button>
          {remap.isSuccess ? (
            <span data-testid="review-remap-started"> {t("review.remap_started")}</span>
          ) : null}
        </Banner>
      ) : null}
      {otherItems.length === 0 ? (
        reviewTop
      ) : (
        <div className="afstack" data-testid="other-review-items">
          {reviewTop}
        </div>
      )}
    </>
  );

  const action = (
    <Card title={t("review.approve_card_title")}>
      {/* D10: the server's own `open_blocking_reviews`, not a client-side count. Zero is the good
          outcome, so it reads as a neutral/positive line, never as a red alarm. */}
      <div className={`countbox${shownBlocking === 0 ? " countbox--ok" : ""}`} data-testid="review-blocking">
        {shownBlocking === 0 ? (
          <span className="countbox__label">{t("review.no_blocking_items")}</span>
        ) : (
          <>
            <span className="countbox__n">{shownBlocking}</span>
            <span className="countbox__label">
              {shownBlocking === 1 ? t("review.open_blocking_one") : t("review.open_blocking_many")}
            </span>
          </>
        )}
      </div>
      {/* xmg-webux (defect 3): once the migration has left `awaiting_mapping_review` there is nothing
          to approve -- the actions are gone, not merely disabled, and the card says why. */}
      {readOnly ? (
        <p className="asidenote" data-testid="review-approve-closed">
          {t("review.approve_closed")}
        </p>
      ) : (
        <>
          <Button
            variant="primary"
            className="button--auth"
            disabled={readOnly || blockingCount > 0 || approvalBusy}
            describedBy={blockingCount > 0 ? "review-blocking-hint" : undefined}
            onClick={() => setConfirming(true)}
          >
            {signoffWatch.waiting || signoff.isPending ? t("signoff.working") : t("review.approve")}
          </Button>
          {signoffWatch.stalled ? (
            <span className="muted" role="status" data-testid="signoff-stalled">
              {t("signoff.still_processing")}
            </span>
          ) : null}
          <span className="asidenote afaction__hint">{t("review.approve_hint")}</span>
          {blockingCount > 0 ? (
            <span className="failnote" id="review-blocking-hint">
              {blockingCount === 1
                ? t("review.approve_blocked_one")
                : t("review.approve_blocked_many", { count: blockingCount })}
            </span>
          ) : null}
          {/*
          §B4.6: the primary path is `signoff_migration` ALONE. `runner.signoff` approves the current version
          when it has not been approved, so "approve then signoff" would be two non-atomic mutations doing
          the work of one. `approve_mapping` stays available as the explicit approve-without-signing-off
          action -- a secondary, explained step, for an operator who wants the approval on record first.
        */}
          <Button
            className="button--auth"
            disabled={readOnly || blockingCount > 0 || approvalBusy}
            onClick={() => void approve.mutateAsync({})}
          >
            {approve.isPending ? t("signoff.working") : t("review.approve_only")}
          </Button>
          <span className="asidenote afaction__hint">{t("review.approve_only_hint")}</span>
        </>
      )}
    </Card>
  );

  return (
    <MigrationPage
      id={id}
      migration={migration.data}
      title={t("review.title")}
      meta={t("migrationheader.meta_route", {
        source: vendorName(migration.data?.source_system ?? ""),
        destination: vendorName(migration.data?.destination_system ?? ""),
      })}
    >
      {readOnly ? <Banner tone="info">{t("review.read_only")}</Banner> : null}
      {autoApproved ? <Banner tone="info">{t("review.auto_approved")}</Banner> : null}

      {/* Critique addendum C2: `awaiting_mapping_review`'s ONLY reachable web placement -- the Status
          screen is the landing only for `loading` onwards and `failed`, so nothing links there earlier. */}
      <HmrcVerificationPanel migrationId={id} />

      <ActionsFirst
        questions={questions}
        action={action}
        actionLabel={t("review.approve_card_title")}
        pinned={state === "awaiting_mapping_review"}
      >
        <Disclosure
          title={t("review.mappings_section")}
          counts={mappingCounts}
          attention={choiceInTabs.length > 0}
          open={mappingsOpen}
          onOpenChange={setMappingsOpen}
          testId="review-mappings"
        >
          <div className="tabs" role="tablist" aria-label={t("review.queues_label")}>
            {TABS.map((key) => (
              <button
                key={key}
                role="tab"
                type="button"
                className="tabs__tab"
                id={`review-tab-${key}`}
                aria-selected={filters.tab === key}
                aria-controls={`review-panel-${key}`}
                onClick={() => {
                  setTarget(null);
                  setTab(key);
                }}
              >
                {TAB_LABELS[key]()} <span className="tabs__count">{`(${counts[key]})`}</span>
              </button>
            ))}
          </div>

          <div className="card card--flush">
            <ReviewFilters />
            <div
              role="tabpanel"
              id={`review-panel-${filters.tab}`}
              aria-labelledby={`review-tab-${filters.tab}`}
              ref={panelRef}
              data-testid="review-tabpanel"
            >
              {versionId === null ? (
                <EmptyState title={t("review.empty")} />
              ) : filters.tab === "coa" ? (
                <CoaTable rows={coaRows} targetKey={targetKey} />
              ) : filters.tab === "vat" ? (
                <VatQueue
                  rows={taxRows}
                  targetKey={targetKey}
                  disabled={readOnly}
                  onResolve={(code, option) => void onResolveByKey("TAX", code, option)}
                />
              ) : filters.tab === "contacts" ? (
                <ContactMerges
                  rows={mergeRows}
                  targetKey={targetKey}
                  disabled={readOnly}
                  onDecide={(sourceExtId, accepted) =>
                    void onResolveByKey("CONTACT", sourceExtId, accepted ? "ACCEPT" : "REJECT")
                  }
                />
              ) : (
                <DimensionLoss
                  items={dimensionItems}
                  targetKey={targetKey}
                  disabled={readOnly}
                  onResolve={(itemId, option) => void onResolveItem(itemId, option)}
                />
              )}
            </div>
          </div>
        </Disclosure>
      </ActionsFirst>

      <ConfirmDialog
        open={confirming}
        title={t("review.approve")}
        onCancel={() => setConfirming(false)}
        confirmDisabled={approvalBusy}
        onConfirm={() => {
          if (approvalBusy) return;
          // §A9.5: no second call. `signoff_migration_task` chains `advance_migration` itself; the screen simply
          // starts polling, which `useMigration`'s refetchInterval already does.
          void signoff.mutateAsync({}).then(() => setConfirming(false));
        }}
      >
        <p>{t("review.approve_confirm_body")}</p>
      </ConfirmDialog>
    </MigrationPage>
  );
}
